import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { mangaList } from "@/mocks/manga";

export default function MangaDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = mangaList.find((m) => m.slug === slug);

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">您要查看的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${manga.title} - 在线免费阅读全集无删减 - 36漫画网`;
  const pageDesc = `${manga.title}，${manga.description.substring(0, 120)}。36漫画网为您提供高清无删减漫画在线阅读，海量正版漫画免费看。`;
  const pageKeywords = `${manga.title},漫画,在线免费阅读,全集无删减,36漫画网,${manga.tags.join(",")}`;

  const breadcrumbItems = [
    { label: "首页", path: "/" },
    { label: "漫画详情", path: "/category/all" },
    { label: manga.title },
  ];

  const mangaSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": manga.title,
    "author": { "@type": "Person", "name": manga.author },
    "description": manga.description,
    "numberOfPages": manga.chapters[0]?.chapterNumber || 0,
  };

  const relatedManga = mangaList
    .filter((m) => m.id !== manga.id && (m.category === manga.category || m.tags.some((t) => manga.tags.includes(t))))
    .slice(0, 4);

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://www.36manhua.com/manga/${manga.slug}`}
        ogType="book"
        ogImage={manga.coverImage}
        schemaJson={mangaSchema}
      />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="rounded-xl overflow-hidden aspect-[3/4]">
                  <img
                    src={manga.coverImage}
                    alt={manga.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className={`text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap ${
                    manga.status === "已完结"
                      ? "bg-accent-100 text-accent-700"
                      : "bg-primary-100 text-primary-700"
                  }`}>
                    {manga.status}
                  </span>
                  {manga.tags.map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {manga.title}
                </h1>

                <div className="flex items-center gap-4 flex-wrap mb-4 text-sm">
                  <span className="text-foreground-600">
                    <i className="ri-user-line mr-1 text-foreground-400"></i>
                    作者：{manga.author}
                  </span>
                  <span className="flex items-center gap-1 text-foreground-600">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong>{manga.rating}</strong> 分
                  </span>
                  <span className="text-foreground-600">
                    <i className="ri-eye-line mr-1 text-foreground-400"></i>
                    {(manga.views / 10000).toFixed(1)} 万次阅读
                  </span>
                  <span className="text-foreground-600">
                    <i className="ri-calendar-line mr-1 text-foreground-400"></i>
                    更新于 {manga.updateDate}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {manga.description}
                </p>

                <div className="flex items-center gap-3 flex-wrap">
                  {manga.chapters.length > 0 && (
                    <Link
                      to={`/manga/${manga.slug}/chapter/${manga.chapters[0].id}`}
                      className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line mr-2"></i>
                      开始阅读
                    </Link>
                  )}
                  <button className="bg-secondary-100 hover:bg-secondary-200 text-secondary-700 px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-2"></i>
                    加入收藏
                  </button>
                  <button className="bg-background-100 hover:bg-background-200 text-foreground-600 px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line mr-2"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chapter List */}
        <div className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <h2 className="text-xl font-bold text-foreground-900 mb-6">
              <i className="ri-list-check mr-2 text-primary-500"></i>
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {manga.chapters.length} 话
              </span>
            </h2>

            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="divide-y divide-background-200/70">
                {manga.chapters.map((chapter, idx) => (
                  <Link
                    key={chapter.id}
                    to={`/manga/${manga.slug}/chapter/${chapter.id}`}
                    className="flex items-center justify-between px-5 py-4 hover:bg-primary-50/50 transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs text-foreground-400 w-8 flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                        {chapter.title}
                      </span>
                      {idx === 0 && (
                        <span className="text-xs bg-accent-500 text-background-50 px-2 py-0.5 rounded-full font-medium flex-shrink-0 whitespace-nowrap">
                          最新
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="text-xs text-foreground-400 hidden sm:block">{chapter.pageCount}页</span>
                      <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                      <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Related Manga */}
        {relatedManga.length > 0 && (
          <div className="w-full px-4 md:px-6 py-14">
            <div className="max-w-[1280px] mx-auto">
              <h2 className="text-xl font-bold text-foreground-900 mb-6">
                <i className="ri-links-line mr-2 text-secondary-500"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {relatedManga.map((rm) => (
                  <Link key={rm.id} to={`/manga/${rm.slug}`} className="group block">
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={rm.coverImage}
                        alt={rm.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                      {rm.title}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      {rm.rating}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}